import React from 'react';
import { Coffee, Home, LucideIcon } from 'lucide-react';
import { RubyLogo } from '../components/RubyLogo';

export interface AdminProductConfig {
  id: string;
  name: string;
  adminTitle: string;
  emailPlaceholder: string;
  Icon: LucideIcon | React.ComponentType<{ size?: number; className?: string }>;
  /** Optional rich heading rendered IN PLACE of `adminTitle` on the login
   *  page. Used by Ruby Till for the "Ruby" + red-pill "Till" wordmark
   *  treatment that matches the rest of the brand. */
  brandHeading?: React.ReactNode;
  /** Tailwind classes for the primary submit button on the login page.
   *  Falls back to the admin-purple theme if not set, so cafemaster and
   *  terrakron keep their original purple admin button. */
  btnClass?: string;
  /** Focus border class for input fields. Falls back to admin-primary. */
  inputFocusClass?: string;
  /** Brand-color text class for inline icons (Key/Lock/Smartphone in MFA
   *  forms). Falls back to text-admin-primary. */
  accentTextClass?: string;
}

const CONFIGS: Record<string, AdminProductConfig> = {
  cafemaster: {
    id: 'cafemaster',
    name: 'Cafe Menu Master',
    adminTitle: 'Cafe Menu Master Admin',
    emailPlaceholder: 'admin@coffeemasterpro.com',
    Icon: Coffee,
  },
  agenticpos: {
    id: 'agenticpos',
    name: 'Ruby Till POS',
    adminTitle: 'Ruby Till',
    emailPlaceholder: 'admin@rubytill.com',
    Icon: RubyLogo,
    brandHeading: React.createElement(
      'span',
      { className: 'inline-flex items-baseline' },
      React.createElement('span', { className: 'text-white font-extrabold' }, 'Ruby'),
      React.createElement(
        'span',
        { className: 'ml-1 px-2 py-0.5 rounded-md bg-red-600 text-white font-black shadow-md shadow-red-900/40' },
        'Till',
      ),
    ),
    btnClass: 'bg-red-600 hover:bg-red-500',
    inputFocusClass: 'focus:border-red-500',
    accentTextClass: 'text-red-500',
  },
  agenticrealestate: {
    id: 'agenticrealestate',
    name: 'Terra Kronos Property Management',
    adminTitle: 'Terra Kronos Admin',
    emailPlaceholder: 'admin@terrakron.com',
    Icon: Home,
  },
};

// Build-time product config injected by Vite from buildProductConfig.ts
declare const __PRODUCT_CONFIG__: { id: string } | undefined;

export function getAdminProductConfig(): AdminProductConfig {
  // 1. Hostname detection first — super-admin-ui serves ONE bundle across
  //    3 product CloudFronts, so the bundle's baked-in __PRODUCT_CONFIG__
  //    would always match the BUILD product (e.g. cafemaster). We want
  //    the branding to match the URL the user is actually on.
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname.toLowerCase();
    if (hostname.includes('rubytill')) return CONFIGS.agenticpos;
    if (hostname.includes('terrakron')) return CONFIGS.agenticrealestate;
    if (hostname.includes('cafemenumaster') || hostname.includes('coffeemasterpro')) return CONFIGS.cafemaster;
  }

  // 2. Fall back to build-time config (SSR/localhost without a matching host)
  if (typeof __PRODUCT_CONFIG__ !== 'undefined' && __PRODUCT_CONFIG__?.id) {
    const buildId = __PRODUCT_CONFIG__.id;
    if (CONFIGS[buildId]) return CONFIGS[buildId];
  }
  return CONFIGS.cafemaster;
}
